import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthSessionService } from '@auth/services/auth-session.service';

/** Set on a request after one 401 refresh-and-retry attempt (avoids infinite retry loops). */
export const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Handles API **401 Unauthorized** without logging out on every failed call.
 *
 * How we know it's 401: `next(req)` sends the HTTP call. If the backend returns
 * 401, Angular's HttpClient fails the observable; `catchError` receives an
 * {@link HttpErrorResponse} and the status code is `error.status` (not the body).
 *
 * Flow:
 * - Not 401 → rethrow (500, 404, etc. unchanged).
 * - 401 on a request already retried (`AUTH_RETRIED`) → rethrow; likely permission
 *   or endpoint auth, not a stale token — user stays logged in.
 * - First 401 → force Keycloak refresh, retry once with the new bearer token.
 * - Refresh fails (or no token after refresh) → session-expired dialog + logout.
 */
export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const authSession = inject(AuthSessionService);
  const keycloak = inject(Keycloak);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only HTTP 401 triggers refresh; other errors go back to the caller as-is.
      if (error.status !== 401) {
        return throwError(() => error);
      }
      // Retry already happened; do not refresh again (avoids loops on real 403-style 401s).
      if (req.context.get(AUTH_RETRIED)) {
        return throwError(() => error);
      }

      // Stale access token: refresh once, then replay the same request with a new Authorization header.
      return authSession.refreshSession().pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            authSession.logoutUnauthorized();
            return throwError(() => error);
          }
          const token = keycloak.token;
          if (!token) {
            authSession.logoutUnauthorized();
            return throwError(() => error);
          }
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            context: req.context.set(AUTH_RETRIED, true),
          });
          return next(retryReq);
        })
      );
    })
  );
};
